<template>
    <v-card
        class="roteirizacao-collapsible-card"
        :class="cardClasses"
        outlined
    >
        <div class="roteirizacao-collapsible-card__header">
            <div class="roteirizacao-collapsible-card__title-row">
                <span class="roteirizacao-collapsible-card__title">{{ title }}</span>
                <HelpGuiasDialog
                    v-if="helpText || (helpItems && helpItems.length)"
                    :helpText="helpText"
                    :helpItems="helpItems"
                    :max-width="helpMaxWidth"
                />
            </div>
            <ExportTablePdf
                v-if="enablePdfExport"
                ref="pdfExporter"
                class="roteirizacao-collapsible-card__pdf-hidden"
                :title="resolvedPdfTitle"
                :subtitle="resolvedPdfSubtitle"
                :sections-provider="resolveLivePdfSections"
                :filename="resolvedPdfFilename"
                :font-size="pdfFontSize"
            />
            <div v-if="$slots['header-actions']" class="roteirizacao-collapsible-card__header-actions">
                <slot name="header-actions" />
            </div>
            <v-btn
                type="button"
                icon
                text
                class="roteirizacao-collapsible-card__toggle"
                :aria-label="expanded ? collapseLabel : expandLabel"
                @click.stop="toggle"
            >
                <v-icon>{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </v-btn>
        </div>
        <div v-show="expanded" class="roteirizacao-collapsible-card__body">
            <slot />
        </div>
    </v-card>
</template>

<script>
import ExportTablePdf from '../ExportTablePdf.vue';
import HelpGuiasDialog from '../HelpGuiasDialog.vue';

export default {
    name: 'RoteirizacaoCollapsibleCard',
    components: {
        ExportTablePdf,
        HelpGuiasDialog,
    },
    data() {
        return {
            pdfSnapshot: null,
            pdfExporting: false,
        };
    },
    props: {
        title: {
            type: String,
            required: true,
        },
        helpText: {
            type: String,
            default: '',
        },
        helpItems: {
            type: Array,
            default: () => [],
        },
        helpMaxWidth: {
            type: [Number, String],
            default: 360,
        },
        value: {
            type: Boolean,
            default: true,
        },
        variant: {
            type: String,
            default: 'default',
        },
        expandLabel: {
            type: String,
            default: '',
        },
        collapseLabel: {
            type: String,
            default: '',
        },
        enablePdfExport: {
            type: Boolean,
            default: false,
        },
        pdfExportDisabled: {
            type: Boolean,
            default: false,
        },
        pdfExportTooltip: {
            type: String,
            default: '',
        },
        pdfTitle: {
            type: String,
            default: '',
        },
        pdfSubtitle: {
            type: String,
            default: '',
        },
        pdfSections: {
            type: Array,
            default: () => [],
        },
        pdfFilename: {
            type: String,
            default: 'export',
        },
        pdfFontSize: {
            type: Number,
            default: 9,
        },
        /** Retorna a instância do painel de custos (ref do componente pai). */
        getCostPanel: {
            type: Function,
            default: null,
        },
        /** Seções adicionais (ex.: motoristas) anexadas ao PDF depois das do painel de custos. */
        getExtraPdfSections: {
            type: Function,
            default: null,
        },
    },
    computed: {
        expanded: {
            get() {
                return this.value;
            },
            set(next) {
                this.$emit('input', next);
            },
        },
        cardClasses() {
            return [
                `roteirizacao-collapsible-card--variant-${this.variant}`,
                this.expanded
                    ? 'roteirizacao-collapsible-card--expanded'
                    : 'roteirizacao-collapsible-card--collapsed'];
        },
        resolvedPdfTitle() {
            if (this.pdfSnapshot && this.pdfSnapshot.title != null) {
                return this.pdfSnapshot.title;
            }
            const explicit = String(this.pdfTitle || '').trim();
            if (explicit) return explicit;
            const panel = this.findCostPanel();
            if (panel && panel.pdfTitleResolved) return panel.pdfTitleResolved;
            return this.title;
        },
        resolvedPdfSubtitle() {
            if (this.findCostPanel()) return '';
            if (this.pdfSnapshot && this.pdfSnapshot.subtitle != null) {
                return this.pdfSnapshot.subtitle;
            }
            return this.pdfSubtitle;
        },
        resolvedPdfFilename() {
            if (this.pdfSnapshot && this.pdfSnapshot.filename) {
                return this.pdfSnapshot.filename;
            }
            return this.pdfFilename;
        },
    },
    methods: {
        findCostPanel() {
            if (typeof this.getCostPanel === 'function') {
                const panel = this.getCostPanel();
                if (panel) return panel;
            }
            const fromSlot = this.findPanelInVNodes(this.$slots.default);
            if (fromSlot) return fromSlot;
            return this.findCostPanelInChildren(this.$children);
        },
        findPanelInVNodes(vnodes) {
            if (!vnodes || !vnodes.length) return null;
            for (let i = 0; i < vnodes.length; i += 1) {
                const vnode = vnodes[i];
                if (!vnode) continue;
                if (vnode.componentInstance
                    && vnode.componentInstance.$options
                    && vnode.componentInstance.$options.name === 'RoteirizacaoCustoPanel') {
                    return vnode.componentInstance;
                }
                if (vnode.children && vnode.children.length) {
                    const nested = this.findPanelInVNodes(vnode.children);
                    if (nested) return nested;
                }
            }
            return null;
        },
        findCostPanelInChildren(components) {
            if (!components || !components.length) return null;
            for (let i = 0; i < components.length; i += 1) {
                const child = components[i];
                if (child && child.$options && child.$options.name === 'RoteirizacaoCustoPanel') {
                    return child;
                }
                const nested = this.findCostPanelInChildren(child.$children);
                if (nested) return nested;
            }
            return null;
        },
        resolveExtraPdfSections() {
            if (typeof this.getExtraPdfSections !== 'function') return [];
            const extra = this.getExtraPdfSections();
            return Array.isArray(extra) ? extra : [];
        },
        resolveLivePdfSections() {
            if (this.pdfSnapshot && Array.isArray(this.pdfSnapshot.sections)) {
                return this.pdfSnapshot.sections;
            }
            if (Array.isArray(this.pdfSections) && this.pdfSections.length) {
                return this.pdfSections;
            }
            const panel = this.findCostPanel();
            const own = panel && Array.isArray(panel.pdfSections) ? panel.pdfSections : [];
            return own.concat(this.resolveExtraPdfSections());
        },
        toggle() {
            const next = !this.value;
            this.$emit('input', next);
            this.$emit('toggle', next);
        },
        async exportPdf(override = {}) {
            const panel = this.findCostPanel();
            const resolvedTitle = override.title != null
                ? override.title
                : (String(this.pdfTitle || '').trim()
                    || (panel && panel.pdfTitleResolved)
                    || this.title);
            this.pdfSnapshot = {
                title: resolvedTitle,
                sections: override.sections != null
                    ? override.sections
                    : ((panel && panel.pdfSections) || []).concat(this.resolveExtraPdfSections()),
                filename: override.filename || this.pdfFilename,
            };
            await this.$nextTick();
            const exp = this.$refs.pdfExporter;
            if (!exp || typeof exp.onBuildClick !== 'function') {
                this.pdfSnapshot = null;
                return;
            }
            this.pdfExporting = true;
            try {
                await exp.onBuildClick(
                    resolvedTitle,
                    panel ? '' : this.resolvedPdfSubtitle,
                    [],
                    [],
                    this.resolvedPdfFilename,
                );
            } finally {
                this.pdfSnapshot = null;
                this.pdfExporting = false;
            }
        },
        onPdfExportClick() {
            if (this.pdfExportDisabled) return;
            this.$emit('export-pdf');
            if (!this.$listeners['export-pdf']) {
                this.exportPdf();
            }
        },
    },
};
</script>

<style scoped>
.roteirizacao-collapsible-card {
    position: relative;
    display: flex;
    flex-direction: column;
    min-height: 0;
    width: 100%;
    overflow: hidden;
    transition: flex 0.3s ease, min-height 0.3s ease;
}
.roteirizacao-collapsible-card--expanded {
    flex: 1 1 0;
    min-height: 0;
}
.roteirizacao-collapsible-card--collapsed {
    flex: 0 0 auto;
}
.roteirizacao-collapsible-card--variant-map.roteirizacao-collapsible-card--expanded {
    flex: 2 1 0;
}
.roteirizacao-collapsible-card--variant-cost.roteirizacao-collapsible-card--expanded {
    flex: 1 1 0;
    min-height: 160px;
    position: relative;
    z-index: 2;
}
.roteirizacao-collapsible-card--variant-table.roteirizacao-collapsible-card--expanded {
    flex: 0 0 auto;
    min-height: auto;
    height: auto;
    overflow: visible;
}
.roteirizacao-collapsible-card__header {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    min-height: 44px;
    padding: 8px 12px 8px 16px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.roteirizacao-collapsible-card__title-row {
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
    padding-right: 8px;
}
.roteirizacao-collapsible-card__title {
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.3;
    min-width: 0;
}
.roteirizacao-collapsible-card__header-actions {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    gap: 8px;
    margin-left: auto;
    margin-right: 8px;
}
/*
 * Os gatilhos do cabeçalho vêm de componentes diferentes (ajuda, upload, export) e cada
 * um traz as próprias margens utilitárias (mb-2/mr-2). O mb-2 esticava o cabeçalho do
 * card que o tivesse, deixando um card mais alto que o outro, e o mr-2 somava ao `gap`
 * de forma irregular. Zerar aqui alinha as alturas e faz o `gap` ser o único espaçamento.
 * `>>>` porque os botões chegam pelo slot, com o escopo do componente pai.
 */
.roteirizacao-collapsible-card__header-actions >>> .v-btn--fab {
    margin: 0 !important;
}
.roteirizacao-collapsible-card__header-actions .roteirizacao-collapsible-card__header-action {
    flex: 0 0 auto;
    margin: 0 !important;
    min-width: 28px !important;
    width: 28px !important;
    height: 28px !important;
    background: transparent !important;
    box-shadow: none !important;
}
.roteirizacao-collapsible-card__header-actions .roteirizacao-collapsible-card__header-action::before {
    opacity: 0 !important;
}
.roteirizacao-collapsible-card__header-actions .roteirizacao-collapsible-card__header-action .v-icon {
    color: #000 !important;
    font-size: 22px !important;
}
.roteirizacao-collapsible-card__toggle {
    flex: 0 0 auto;
    margin: 0 !important;
    min-width: 28px !important;
    width: 28px !important;
    height: 28px !important;
    background: transparent !important;
    box-shadow: none !important;
}
.roteirizacao-collapsible-card__toggle::before {
    opacity: 0 !important;
}
.roteirizacao-collapsible-card__toggle .v-icon {
    color: #000 !important;
    font-size: 26px !important;
}
.roteirizacao-collapsible-card__body {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
/*
 * Card de custos: o painel de custo e o de motoristas (KPIs, Gantt, tabelas) dividem o corpo.
 * O corpo rola inteiro; o painel de custo deixa de ter scroll próprio para não ser espremido
 * pelo de motoristas (que antes ficava cortado pelo overflow hidden).
 */
.roteirizacao-collapsible-card--variant-cost .roteirizacao-collapsible-card__body {
    overflow-y: auto;
    overflow-x: hidden;
}
.roteirizacao-collapsible-card--variant-cost .roteirizacao-collapsible-card__body >>> .roteirizacao-custo-panel {
    flex: 0 0 auto;
    overflow: visible;
}
.roteirizacao-collapsible-card--variant-cost .roteirizacao-collapsible-card__body >>> .roteirizacao-motoristas-panel {
    flex: 0 0 auto;
    min-width: 0;
    padding: 0 16px 16px;
}
.roteirizacao-collapsible-card--variant-table .roteirizacao-collapsible-card__body {
    flex: 0 0 auto;
    min-height: auto;
    height: auto;
    display: block;
    overflow: visible;
}
.roteirizacao-collapsible-card--collapsed .roteirizacao-collapsible-card__body {
    display: none !important;
}
.roteirizacao-collapsible-card__pdf-hidden {
    display: none !important;
}
</style>
